import { z } from 'zod';
import { patchSchema } from './common.ts';
import { permissionSetSchema, roleSchema, type PermissionSet, type Role } from '../rbac.ts';

export const loginNameSchema = z
  .string()
  .trim()
  .min(3)
  .max(64)
  .regex(/^[a-z0-9._-]+$/i, 'Faqat lotin harflari, raqamlar va . _ -');

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ()-]{7,20}$/, "Telefon raqami noto'g'ri");

/** Body of the employee form ("Xodim qo'shish": Umumiy ma'lumot + Huquqlar). */
export const createMemberSchema = z.object({
  fullName: z.string().trim().min(2).max(160),
  position: z.string().trim().max(160).optional(),
  phone: phoneSchema.optional(),
  login: loginNameSchema,
  password: z.string().min(8).max(256),
  isActive: z.boolean().default(true),
  avatarFileId: z.uuid().nullish(),
  role: roleSchema.default('tenant_member'),
  permissions: permissionSetSchema.nullish(),
});
export type CreateMemberInput = z.infer<typeof createMemberSchema>;

export const updateMemberSchema = patchSchema(createMemberSchema.omit({ password: true })).extend({
  /** "" clears the phone number */
  phone: z.union([phoneSchema, z.literal('')]).optional(),
  password: z.string().min(8).max(256).optional(),
  /** Move the membership to another reservoir of the same organization ("SUV OMBORI"; organization admins only). */
  tenantId: z.uuid().optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberSchema>;

/** `GET …/members/login-available?login=&userId=` — is a login free (case-insensitive), and the first free variant. */
export const loginAvailableQuerySchema = z.object({
  login: z.string().trim().min(1).max(64),
  /** the member being edited — its own login counts as available */
  userId: z.uuid().optional(),
});
export interface LoginAvailableDto {
  login: string;
  available: boolean;
  /** `login` itself when free, otherwise the first free `login2`, `login3`, … */
  suggestion: string;
}

export interface MemberDto {
  userId: string;
  membershipId: string;
  fullName: string;
  login: string;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  role: Role;
  permissions: PermissionSet | null;
  organizationId: string | null;
  tenantId: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  avatarFileId: string | null;
  /** "Xodimni bloklash": a blocked user cannot sign in anywhere (differs from `isActive = false`) */
  blockedAt: string | null;
}

/** GET /members/:userId also tells who blocked the employee. */
export interface MemberDetailDto extends MemberDto {
  blockedBy: { id: string; fullName: string } | null;
}

/** Panel of an audit entry ("Admin panel", "Monitoring panel"; `auth` = sign-in events). */
export type AuditPanel = 'auth' | 'admin' | 'monitoring';
export type AuditOutcome = 'success' | 'denied' | 'error';

export interface AuditLogDto {
  id: string;
  actor: { id: string; fullName: string } | null;
  action: string;
  entityType: string;
  entityId: string | null;
  meta: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
  /** human-readable sequence ("LOG-48213") */
  seq: number | null;
  /** stored panel, or derived from the action when the writer did not set one */
  panel: AuditPanel | null;
  /** admin module / monitoring menu key (stored or derived from the action) */
  module: string | null;
  outcome: AuditOutcome;
  userAgent: string | null;
}

export const MEMBER_LOG_KINDS = ['all', 'error', 'admin', 'monitoring'] as const;
export type MemberLogKind = (typeof MEMBER_LOG_KINDS)[number];

const csvList = z
  .string()
  .max(2000)
  .transform((v) => v.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 100))
  .optional();

/**
 * Keyset cursor of the log list: `<createdAt, UTC with microseconds>_<id>` of the last entry of the previous page
 * (`MemberLogsDto.nextCursor`). Unlike `page`, it never repeats or skips entries written between two page loads.
 */
export const LOG_CURSOR_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `GET …/members/:userId/logs` ("Loglar"): filters of the log list. */
export const memberLogsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  /** entries after this cursor (newest first); `page` is ignored when set */
  cursor: z.string().regex(LOG_CURSOR_PATTERN).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().max(200).optional(),
  /** "Barchasi" | "Xatolar" (denied/failed) | "Admin panel" | "Monitoring" */
  kind: z.enum(MEMBER_LOG_KINDS).default('all'),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  /** audit actions / module keys whose translated labels match `q` (resolved by the client), OR-ed with the text match */
  actions: csvList,
  modules: csvList,
  /** IANA time zone for the per-day counts (default UTC) */
  tz: z.string().trim().max(64).optional(),
});
export type MemberLogsQuery = z.input<typeof memberLogsQuerySchema>;

export interface MemberLogsDto {
  items: AuditLogDto[];
  page: number;
  pageSize: number;
  /** entries matching every filter */
  total: number;
  /** per-kind counts inside q / from / to (the filter chips: "Xatolar 12") */
  counts: Record<MemberLogKind, number>;
  /** entries per local day (`tz`) matching every filter, newest first ("8 ta yozuv") */
  days: Array<{ day: string; count: number }>;
  /** all entries of the employee in this tenant, unfiltered ("Loglar 1 248") */
  overall: number;
  /** cursor of the next page (`?cursor=`); `null` on the last page */
  nextCursor?: string | null;
}
